import { Extension } from '@tiptap/core';
import { Suggestion, type SuggestionOptions } from '@tiptap/suggestion';
import type { BuiltinCommand, SkillCommand, SlashCommand } from './types';

export type SlashCommandOptions = {
  builtinCommands: BuiltinCommand[];
  /**
   * Read as a getter, not an array: the plugin below is built once when the
   * editor is created, and tiptap's setOptions cannot rebuild it. Skills load
   * async, so an array captured here would stay empty forever.
   */
  getSkillCommands: () => SkillCommand[];
  suggestion: Partial<SuggestionOptions<SlashCommand>>;
};

export const SlashCommandExtension = Extension.create<SlashCommandOptions>({
  name: 'slashCommand',

  addOptions() {
    return {
      builtinCommands: [],
      getSkillCommands: () => [],
      suggestion: {},
    };
  },

  addProseMirrorPlugins() {
    // Strip items/command from overrides so consumers can't replace core logic
    const { items: _items, command: _command, ...suggestionOverrides } = this.options.suggestion;

    return [
      Suggestion<SlashCommand>({
        editor: this.editor,
        char: '/',
        // Allow slash commands anywhere — we filter builtins to start-of-line
        // in the items callback below.
        startOfLine: false,
        ...suggestionOverrides,
        items: ({ query, editor }) => {
          const lowerQuery = query.toLowerCase();
          const matchesQuery = (cmd: SlashCommand) =>
            cmd.name.toLowerCase().includes(lowerQuery) || cmd.description.toLowerCase().includes(lowerQuery);

          const matchingSkills = this.options.getSkillCommands().filter(matchesQuery);

          // Only include builtins when the / is at the start of a line.
          // Check the character before the slash trigger position.
          const { from } = editor.state.selection;
          // The trigger char occupies position (from - query.length - 1)
          const triggerPos = from - query.length - 1;
          // Pass '\n' as blockSeparator so paragraph/hard-break boundaries
          // produce newline characters in the extracted text.
          const isAtLineStart =
            triggerPos <= 1 || editor.state.doc.textBetween(triggerPos - 1, triggerPos, '\n') === '\n';

          if (isAtLineStart) {
            const matchingBuiltins = this.options.builtinCommands.filter(matchesQuery);
            return [...matchingSkills, ...matchingBuiltins];
          }

          return matchingSkills;
        },
        command: ({ editor, range, props: item }) => {
          if (item.type === 'builtin') {
            editor.chain().focus().deleteRange(range).run();
            item.execute();
          } else if (item.type === 'skill') {
            // Replace typed text with canonical /skill-name + trailing space
            editor.chain().focus().deleteRange(range).insertContent(`/${item.slug} `).run();
          }
        },
      }),
    ];
  },
});
